// Utilitaires de sécurité pour l'application

// Validation des entrées utilisateur
export class InputValidator {
  // Validation des emails
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Validation des mots de passe
  static isValidPassword(password: string): boolean {
    // Au moins 8 caractères, 1 majuscule, 1 minuscule, 1 chiffre
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  }

  // Validation des noms (lettres, espaces, tirets, apostrophes)
  static isValidName(name: string): boolean {
    const nameRegex = /^[a-zA-ZÀ-ÿ\s\-']{2,50}$/;
    return nameRegex.test(name);
  }

  // Validation des numéros de téléphone
  static isValidPhone(phone: string): boolean {
    const phoneRegex = /^[+]?[0-9\s\-()]{10,15}$/;
    return phoneRegex.test(phone);
  }

  // Validation des URLs
  static isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  // Validation des coordonnées GPS
  static isValidCoordinates(lat: number, lng: number): boolean {
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  // Nettoyage des chaînes de caractères
  static sanitizeString(input: string): string {
    return input
      .trim()
      .replace(/[<>]/g, '') // Supprimer les balises HTML
      .replace(/javascript:/gi, '') // Supprimer les protocoles dangereux
      .replace(/on\w+=/gi, ''); // Supprimer les événements JavaScript
  }

  // Validation des fichiers
  static isValidFileType(file: File, allowedTypes: string[]): boolean {
    return allowedTypes.includes(file.type);
  }

  static isValidFileSize(file: File, maxSizeMB: number): boolean {
    return file.size <= maxSizeMB * 1024 * 1024;
  }
}

// Protection contre les attaques XSS
export class XSSProtection {
  // Encodage HTML
  static encodeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Décodage HTML sécurisé
  static decodeHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  }

  // Validation des données JSON
  static safeJsonParse(jsonString: string): any {
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  }
}

// Protection CSRF
export class CSRFProtection {
  private static token: string | null = null;

  static getToken(): string | null {
    return this.token;
  }

  static setToken(token: string): void {
    this.token = token;
  }

  static generateToken(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  static validateToken(token: string): boolean {
    return token === this.token;
  }
}

// Gestion des sessions
export class SessionManager {
  private static readonly SESSION_KEY = 'osint_session';
  private static readonly SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  static createSession(userId: string, userData: any): void {
    const session = {
      userId,
      userData,
      createdAt: Date.now(),
      expiresAt: Date.now() + this.SESSION_TIMEOUT
    };
    localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
  }

  static getSession(): any {
    const sessionStr = localStorage.getItem(this.SESSION_KEY);
    if (!sessionStr) return null;

    try {
      const session = JSON.parse(sessionStr);
      if (Date.now() > session.expiresAt) {
        this.clearSession();
        return null;
      }
      return session;
    } catch {
      this.clearSession();
      return null;
    }
  }

  static clearSession(): void {
    localStorage.removeItem(this.SESSION_KEY);
  }

  static isSessionValid(): boolean {
    return this.getSession() !== null;
  }

  static refreshSession(): void {
    const session = this.getSession();
    if (session) {
      session.expiresAt = Date.now() + this.SESSION_TIMEOUT;
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
    }
  }
}

// Protection des données sensibles
export class DataProtection {
  // Chiffrement simple des données sensibles (pour le développement)
  static encrypt(data: string): string {
    // En production, utiliser une bibliothèque de chiffrement robuste
    return btoa(encodeURIComponent(data));
  }

  static decrypt(encryptedData: string): string {
    try {
      return decodeURIComponent(atob(encryptedData));
    } catch {
      return '';
    }
  }

  // Masquage des données sensibles
  static maskSensitiveData(data: string, type: 'email' | 'phone' | 'creditCard'): string {
    switch (type) {
      case 'email':
        const [local, domain] = data.split('@');
        return `${local.charAt(0)}***@${domain}`;
      case 'phone':
        return data.replace(/(\d{2})\d{6}(\d{2})/, '$1******$2');
      case 'creditCard':
        return data.replace(/\d(?=\d{4})/g, '*');
      default:
        return data;
    }
  }
}

// Validation des permissions
export class PermissionManager {
  private static userPermissions: string[] = [];

  static setPermissions(permissions: string[]): void {
    this.userPermissions = permissions;
  }

  static hasPermission(permission: string): boolean {
    return this.userPermissions.includes(permission);
  }

  static hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  static hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }
}

// Protection contre les attaques par injection
export class InjectionProtection {
  // Validation des paramètres SQL (pour les requêtes côté client)
  static sanitizeSqlParam(param: string): string {
    return param.replace(/['";\\]/g, '');
  }

  // Validation des paramètres d'URL
  static sanitizeUrlParam(param: string): string {
    return encodeURIComponent(param);
  }

  // Validation des paramètres de recherche
  static sanitizeSearchParam(param: string): string {
    return param.replace(/[<>]/g, '').trim();
  }
}

// Gestion des erreurs sécurisée
export class SecureErrorHandler {
  static handleError(error: any, context: string): void {
    // Log l'erreur de manière sécurisée
    console.error(`[${context}] Error:`, {
      message: error.message,
      stack: process.env.NODE_ENV === 'development' ? error.stack : undefined,
      timestamp: new Date().toISOString()
    });

    // En production, envoyer à un service de monitoring
    if (process.env.NODE_ENV === 'production') {
      // this.sendToMonitoring(error, context);
    }
  }

  static sanitizeErrorMessage(error: any): string {
    // Ne pas exposer d'informations sensibles dans les messages d'erreur
    if (error.status === 401) {
      return 'Authentification requise';
    } else if (error.status === 403) {
      return 'Accès refusé';
    } else if (error.status === 404) {
      return 'Ressource non trouvée';
    } else if (error.status >= 500) {
      return 'Erreur serveur';
    } else {
      return 'Une erreur est survenue';
    }
  }
}

// Validation des formulaires
export class FormValidator {
  static validateRequired(value: any): boolean {
    return value !== null && value !== undefined && value.toString().trim() !== '';
  }

  static validateMinLength(value: string, minLength: number): boolean {
    return value.length >= minLength;
  }

  static validateMaxLength(value: string, maxLength: number): boolean {
    return value.length <= maxLength;
  }

  static validatePattern(value: string, pattern: RegExp): boolean {
    return pattern.test(value);
  }

  static validateCustom(value: any, validator: (value: any) => boolean): boolean {
    return validator(value);
  }
} 